import React from "react";
import { motion } from "framer-motion";
import { Wrench, UserCheck, Layers } from "lucide-react";
import Chapter from "../site/Chapter";

const points = [
  {
    icon: Wrench,
    title: "Built around how you work",
    body: "We design workflows around your roles, your tools, and your process — not a generic template.",
  },
  {
    icon: UserCheck,
    title: "A person behind every output",
    body: "Every AI-assisted step is reviewed by a trained specialist who understands the work and owns the result.",
  },
  {
    icon: Layers,
    title: "One operating layer, not ten tools",
    body: "We unify the tools and channels involved into a single, transparent, repeatable process.",
  },
];

export default function HomeApproach() {
  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="relative py-24 sm:py-32 border-y border-slate-900 bg-[#070F1F]"
    >
      <div className="absolute inset-0 bg-radial-cyan opacity-60" />
      <div className="absolute inset-0 bg-grain" />
      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Chapter
              number="02"
              eyebrow="The A4 Assist Approach"
              accent="lime"
              title={
                <>
                  AI workflows.{" "}
                  <span className="font-serif italic">Human</span> specialists.
                  <br /> One partner.
                </>
              }
            />
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed"
            >
              A4 Assist plugs into your business as a dedicated operating layer
              — for recruiting, for day-to-day operations, or for data and
              research work. We set up the AI-enabled workflow, we run it, and
              we put a trained specialist behind every output, so your team
              gets finished work, not more tools to manage.
            </motion.div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {points.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`approach-point-${i}`}
                className={`rounded-xl border border-slate-800 bg-[#0F1A30]/60 backdrop-blur-sm p-6 hover:border-[#00E5FF]/40 transition-colors ${
                  i === 2 ? "sm:col-span-2" : ""
                }`}
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20">
                  <p.icon size={18} />
                </div>
                <h3 className="font-display mt-4 text-base font-semibold text-white">
                  {p.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
